import './AppLauncher.css';

import { setEnvironment } from '@vse/environment';
import { standalone } from '@vse/main';
import * as machinemotion from '@vse/api/machinemotion';
import components from '@vse/components';
import appControl from './appControl.js';
import { pythonControl } from './pythonControl';
import { LaunchableApp } from './LaunchableApp';
import {
  APPLICATION_LIST_QUERY_SELECTOR,
  APPLICATION_STATUS_CONTAINER_QUERY_SELECTOR,
  ApplicationCard,
  ApplicationControls,
  ApplicationList,
  PLAYING_APPLICATION_NAME_QUERY_SELECTOR,
  SEQUENCE_LIST_QUERY_SELECTOR,
  SEQUENCE_LIST_TITLE_QUERY_SELECTOR,
  SequenceList,
} from './components';
import { messageBox } from '@src/style/style';

/**
 * @param {Library} library The MachineLogic Library.
 * @param {Navigate} navigate A function to navigate to another page.
 * @returns {void} Nothing.
 */
export function initializeAppLauncher (library, navigate) {
  setEnvironment('pendant', 'launcher');

  renderAppLauncher();
  standalone(library, navigate, { renderUi: false });

  initializeApplications(library)
    .then(verifyAutoLaunchSettings)
    .then(verifyMachineLogicApplications)
    .then(verifyPythonApplications)
    .then(traceData => initializeApplicationListUi(library, traceData));
}

/** @type {AppLauncherGlobalVariables} */
const gGlobalVariables = {
  // TODO GD This is used to identify the selected application, not just the running application
  currentPlayingApp: undefined, // currently running application --> read and write

  appStatus: undefined, // status of currentPlayingApp   --> read and write
  playButtonOnClick: undefined, // function executed when clicking on the playButton. Gets populated during execution.
  runtimeConfig: {},
};

// TODO GD Get rid of these
const gMmAppsIndexes = []; // list of indexes of the mmApps --> only read
const gPyAppsIndexes = []; // list of indexes of the Py and Bash apps

// TODO GD Use gCustomApplications.length
// TODO GD Push to gCustomApplications instead of inserting by index with gNumberOfApps
let gNumberOfApps = 0; // number of applications
/** @type LaunchableApp[] */
let gCustomApplications; // list of Applications

/**
 * Initialize the MachineLogic and Python applications.
 *
 * @param {import('@vse/machinelogic/Library').Library} library The MachineLogic library.
 * @returns {Promise} A promise that resolves once all applications have been initialized.
 */
function initializeApplications (library) {
  gNumberOfApps = 0;
  gCustomApplications = [];

  initializeMachineLogicApplications(library);

  return initializePythonApplications();
}

/**
 * Creates LaunchableApps from the MachineLogic applications in the library.
 * Adds the MachineLogic LaunchableApps to the gCustomApplications.
 *
 * @param {import('@vse/machinelogic/Library').Library} library The MachineLogic library.
 * @returns {void} Nothing.
 */
function initializeMachineLogicApplications (library) {
  const configManager = library.getConfigManager();

  // TODO GD We do this check somewhere in machinelogic already, probably
  if (
    !configManager.listRobots().length &&
    !configManager.listInputs().length &&
    !configManager.listOutputs().length &&
    !configManager.listAxes().length &&
    library.listApplications().length > 0
  ) {
    console.warn('Please configure your machine before launching MachineLogic applications.');
    messageBox('Please configure your machine before launching MachineLogic applications.', 'warning');

    return;
  }

  library.listApplications().forEach(applicationName => {
    const application = library.getApplication(applicationName);
    const appNumber = gNumberOfApps++ + 1;
    gMmAppsIndexes.push(appNumber);

    /** @type LaunchableAppData */
    const launchableAppData = {
      number: appNumber,
      name: applicationName,
      autoLaunch: application.autoLaunch,
      machineLogicApplication: application,
    };

    gCustomApplications[appNumber] = new LaunchableApp(gGlobalVariables, launchableAppData);
  });
}

/**
 * Fetches the python applications and creates Python LaunchableApps.
 * Adds the Python LaunchableApps to the gCustomApplications.
 *
 * @returns {Promise} A promise that is resolved once all Python LaunchableApps have been added to gCustomApplications.
 */
function initializePythonApplications () {
  return new Promise(resolve => {
    pythonControl.library(pythonApplications => {
      if (!pythonApplications) {
        resolve();

        return;
      }

      pythonApplications.forEach(pythonApplication => {
        const appNumber = gNumberOfApps++ + 1;
        gPyAppsIndexes.push(appNumber);

        /** @type LaunchableAppData */
        const launchableAppData = {
          number: appNumber,
          name: pythonApplication.name,
          extension: pythonApplication.extension,
          autoLaunch: pythonApplication.autoLaunch,
          commands: pythonApplication.commands,
          lastModifiedDate: pythonApplication.modif_date,
        };

        gCustomApplications[appNumber] = new LaunchableApp(gGlobalVariables, launchableAppData);
      });

      resolve();
    });
  });
}

/**
 * Verifies that only one app has the auto-launch feature.
 * If multiple apps have auto-launch enabled, they will all be disabled except the first one.
 *
 * @returns {Promise} A promise that resolves when all auto launch settings have been verified.
 */
function verifyAutoLaunchSettings () {
  let autoLaunchDetected = false;
  const autoLaunchOffRequests = [];

  for (let i = 1; i <= gNumberOfApps; i++) {
    const app = gCustomApplications[i];

    if (app.autoLaunch) {
      // Only 1 app should auto launch
      if (autoLaunchDetected) {
        console.log(`Auto-launch conflict : ${app.name} must be deactivated.`);
        autoLaunchOffRequests.push(appControl.turnOffApplicationAutoLaunch(app));
      }

      autoLaunchDetected = true;
    }
  }

  return Promise.all(autoLaunchOffRequests)
    .catch(error => {
      console.log(error);
    });
}

/**
 * Verifies if a MachineLogic app is already playing when opening the app launcher.
 * Will fail if the machine motion status cannot be retrieved.
 *
 * @returns {Promise<ExecutionState | undefined>} A promise that resolves when the MachineLogic applications have been verified, with the machine motion status if an app is running.
 */
function verifyMachineLogicApplications () {
  if (gMmAppsIndexes.length === 0) {
    return Promise.resolve();
  }

  // Look for an already executing application
  return new Promise((resolve, reject) => {
    machinemotion.status(machineLogicApplicationStatus => {
      if (!machineLogicApplicationStatus) {
        reject(new Error('Error while polling the machine logic application state'));

        return;
      }

      // Check if a MachineLogic app is executing
      if (
        machineLogicApplicationStatus.state === appControl.gStateEnum.completed ||
        machineLogicApplicationStatus.state === appControl.gStateEnum.inactive ||
        machineLogicApplicationStatus.state === appControl.gStateEnum.not_started
      ) {
        // No MachineLogic app is executing, we're done here.
        resolve();

        return;
      }

      // Set selected application
      gMmAppsIndexes.forEach(appIndex => {
        const app = gCustomApplications[appIndex];
        if (app.name === machineLogicApplicationStatus.application && app.extension === 'mm') {
          gGlobalVariables.currentPlayingApp = app;
          // Update the appStatus
          if (machineLogicApplicationStatus.state === appControl.gStateEnum.running || machineLogicApplicationStatus.state === appControl.gStateEnum.paused) {
            gGlobalVariables.appStatus = machineLogicApplicationStatus.state;
          }
        }
      });

      // Pass the MachineLogic app status along
      resolve(machineLogicApplicationStatus);
    });
  });
}

/**
 * Verifies if a Python app is already playing when opening the app launcher.
 * Will fail if the python server status cannot be retrieved.
 *
 * @param {ExecutionState} [machineLogicApplicationStatus] The machine motion status. This is expected to be undefined if no MachineLogic application is running.
 * @returns {Promise<ExecutionState | undefined>} A promise that resolves when the Python applications have been verified, with the python or machine motion status if an app is running.
 */
function verifyPythonApplications (machineLogicApplicationStatus = undefined) {
  if (gPyAppsIndexes.length === 0) {
    return Promise.resolve(machineLogicApplicationStatus);
  }

  // Look for an already executing application
  return new Promise((resolve, reject) => {
    pythonControl.status(pythonApplicationStatus => {
      if (!pythonApplicationStatus) {
        reject(new Error('Error while polling the python application state'));

        return;
      }

      // Check if a python app is executing
      if (
        pythonApplicationStatus.state === appControl.gStateEnum.completed ||
        pythonApplicationStatus.state === appControl.gStateEnum.inactive
      ) {
        // No Python app is executing, pass the MachineLogic app status along
        resolve(machineLogicApplicationStatus);

        return;
      }

      /// A Python app is executing, check if a MachineLogic app is also executing (gGlobalVariables.currentPlayingApp would be set before we get here)
      if (gGlobalVariables.currentPlayingApp === undefined) {
        // No MachineLogic app is running, set the python app as running.
        gPyAppsIndexes.forEach(appIndex => {
          const app = gCustomApplications[appIndex];
          if (app.name === pythonApplicationStatus.application && app.extension !== 'mm') {
            gGlobalVariables.currentPlayingApp = app;
            // Update the appStatus
            if (pythonApplicationStatus.state === appControl.gStateEnum.running || pythonApplicationStatus.state === appControl.gStateEnum.paused) {
              gGlobalVariables.appStatus = pythonApplicationStatus.state;
            }
          }
        });

        // Pass the python app status along
        resolve(pythonApplicationStatus);

        return;
      }

      // A python and a MachineLogic app are running.
      // MachineLogic has priority, stop the python app.
      pythonControl.stop(pythonApplicationStatus.application, stopWasSuccessful => {
        if (!stopWasSuccessful) {
          reject(new Error('Error while stopping the python application'));
        }

        // Pass the MachineLogic app status along
        resolve(machineLogicApplicationStatus);
      });
    });
  });
}

/**
 * Fills the UI that contains the list of available applications
 *
 * @param {import('@vse/machinelogic/Library').Library} library The MachineLogic library.
 * @param {ExecutionState} [applicationStatus] The application status of the currently running Python or MachineLogic application.
 * @returns {void} Nothing.
 */
function initializeApplicationListUi (library, applicationStatus = undefined) {
  updateApplicationList(library, gCustomApplications);

  $(`#App${gGlobalVariables.currentPlayingApp?.number || 1}`)[0]?.scrollIntoView({ behavior: 'smooth' });

  // If an app is already playing, update display
  if (gGlobalVariables.currentPlayingApp) {
    // Update app name display at top right of the screen
    $(PLAYING_APPLICATION_NAME_QUERY_SELECTOR).text(gCustomApplications[gGlobalVariables.currentPlayingApp.number].name);
    // Fill in the right panel, update the status + button functions
    updateSequenceList(library, gGlobalVariables.currentPlayingApp.number);

    if (applicationStatus) {
      appControl.onTrace(applicationStatus, gGlobalVariables);
    }
  }
}

/**
 * Creates the app launcher UI.
 * It consists of:
 * - A header containing the pane titles and the application name and status
 * - A left pane showing the available applications
 * - A right pane showing the sequences of the selected application
 * - A bottom section containing application controls (play, pause, stop)
 */
function renderAppLauncher () {
  const lContainer = $('<div>').attr('id', 'app_launcher_container');
  const lApplicationListContainer = $('<div>').attr('id', 'application_list_container');
  const lSelectedApplicationContainer = $('<div>').attr('id', 'selected_application_container');
  const lSequenceListContainer = $('<div>').attr('id', 'sequence_list_container');
  const lApplicationControlsContainer = $('<div>').attr('id', 'application_controls_container');

  $('#guiContainer').append(
    lContainer.append(
      lApplicationListContainer.append(
        ApplicationList(),
      ),
      lSelectedApplicationContainer.append(
        lSequenceListContainer.append(
          SequenceList(),
        ),
        lApplicationControlsContainer.append(
          ApplicationControls({
            onPlay: () => gGlobalVariables.playButtonOnClick(),
            onPause: () => appControl.pauseApplication(gMmAppsIndexes, gGlobalVariables),
            onStop: () => appControl.stopApplication(gMmAppsIndexes, gGlobalVariables),
          }),
        ),
      ),
    ),
  );

  gGlobalVariables.playButtonOnClick = () => appControl.playApplication(gMmAppsIndexes, gGlobalVariables);
}

/**
 * Renders the application list
 */
function updateApplicationList (pLibrary, pLaunchableApplications) {
  const applicationListContainer = $(APPLICATION_LIST_QUERY_SELECTOR).empty();

  for (let appIndex = 1; appIndex <= gNumberOfApps; appIndex++) {
    const app = pLaunchableApplications[appIndex];
    const id = `App${appIndex}`;
    const isSelected = gGlobalVariables.currentPlayingApp?.number === app.number;

    applicationListContainer.append(
      ApplicationCard({
        app,
        id,
        isSelected,
        onClick: () => {
          gGlobalVariables.currentPlayingApp = app;
          updateApplicationList(pLibrary, pLaunchableApplications);
          updateSequenceList(pLibrary, appIndex);
        },
        onSelectAutoLaunch: () => enableAutoLaunch(appIndex),
      }),
    );
  }
}

// In case the auto-launched app is changed
function enableAutoLaunch (selectedAppIndex) {
  // get old
  let previousAutoLaunchApplicationIndex;
  Array.from(document.getElementsByClassName('icon-boot-up')).forEach(function (value, index) {
    if (value.className === 'icon-boot-up auto-launch-on') {
      previousAutoLaunchApplicationIndex = index;
    }
  });

  // set new
  function set_new () {
    if (selectedAppIndex - 1 !== previousAutoLaunchApplicationIndex) {
      const selected_app = gCustomApplications[selectedAppIndex];
      if (selected_app.isMachineLogicApplication()) {
        machinemotion.auto_launch_on(selected_app.name, function (pData) {
          if (pData) { // success
            selected_app.autoLaunch = true;
            // Update display
            Array.from(document.getElementsByClassName('icon-boot-up'))[selectedAppIndex - 1].className = 'icon-boot-up auto-launch-on';
          }
        });
      }
      else {
        pythonControl.turnOnAutoLaunch(selected_app.name, function (pData) {
          if (pData) { // success
            selected_app.autoLaunch = true;
            // Update display
            Array.from(document.getElementsByClassName('icon-boot-up'))[selectedAppIndex - 1].className = 'icon-boot-up auto-launch-on';
          }
        });
      }
    }
  }

  // remove old
  if (previousAutoLaunchApplicationIndex !== undefined) {
    const previousApplication = gCustomApplications[previousAutoLaunchApplicationIndex + 1];
    if (previousApplication.isMachineLogicApplication()) {
      machinemotion.auto_launch_off(previousApplication.name, function (pData) {
        if (pData) { // success
          previousApplication.autoLaunch = false;
          // Update display
          document.getElementsByClassName('icon-boot-up')[previousAutoLaunchApplicationIndex].className = 'icon-boot-up auto-launch-off';
          set_new();
        }
      });
    }
    else {
      pythonControl.turnOffAutoLaunch(previousApplication.name, function (pData) {
        if (pData) { // success
          previousApplication.autoLaunch = false;
          // Update display
          document.getElementsByClassName('icon-boot-up')[previousAutoLaunchApplicationIndex].className = 'icon-boot-up auto-launch-off';
          set_new();
        }
      });
    }
  }
  // else directly set the new auto-launch app
  else {
    set_new();
  }
}

/**
 * Function to get the sequences of an app
 */
function updateSequenceList (library, selected_app) {
  // Clear info in the meantime
  const nextCurrentPlayingApp = gCustomApplications[selected_app];
  gGlobalVariables.currentPlayingApp = undefined;

  const lGenericContainer = $(SEQUENCE_LIST_QUERY_SELECTOR).css('border', '4px solid #EEF1F5').css('background-color', '#EEF1F5');

  // Clear previous information
  lGenericContainer.html('');

  gGlobalVariables.runtimeConfig = {};

  if (nextCurrentPlayingApp.isMachineLogicApplication() && nextCurrentPlayingApp.getMachineLogicApplication().hasUi()) { // TODO: This may be an improper test here. Maybe we have a "disabled" boolean instead
    $(SEQUENCE_LIST_TITLE_QUERY_SELECTOR).text('UI');
    $(APPLICATION_STATUS_CONTAINER_QUERY_SELECTOR).show();

    const lMachineLogicApplication = nextCurrentPlayingApp.getMachineLogicApplication();

    // scaling UI widgets defined in UI Builder for app launcher view
    // multiplier calculation example : app launcher width / ui builder width
    const widthMultiplier = 2.0685;
    const heightMultiplier = 1.6417;

    const applicationUI = lMachineLogicApplication.getUi();
    applicationUI.scaleAllWidgets(widthMultiplier, heightMultiplier);

    const lUiInstance = components.renderUiPlay(library, lMachineLogicApplication, applicationUI, null, {
      setRuntimeConfig: function (pRuntimeConfig) {
        gGlobalVariables.runtimeConfig = Object.assign(gGlobalVariables.runtimeConfig, pRuntimeConfig);
      },
      setTraceCallback: function (pOnTrace) {
        nextCurrentPlayingApp.setOnTrace(pOnTrace);
      },
      setStateChangedCallback: function (pOnStateChanged) {
        nextCurrentPlayingApp.setOnStateChanged(pOnStateChanged);
      },
    });

    // Descale ui widget dimensions back to original, mainly for download and upload app to cad or if using ui builder in pendant
    lUiInstance.on('remove', () => {
      applicationUI.scaleAllWidgets(1 / widthMultiplier, 1 / heightMultiplier);
    });

    // Add App Launcher specific styles to UI play mode here
    lUiInstance.find('.ml_ui_builder_header').hide();
    lGenericContainer.append(lUiInstance);
  }
  else {
    $(SEQUENCE_LIST_TITLE_QUERY_SELECTOR).text('Sequence Status');
    $(APPLICATION_STATUS_CONTAINER_QUERY_SELECTOR).show();

    // For each sequence
    nextCurrentPlayingApp.sequences.forEach(function (value, index, array) {
      // Ignore #startup sequence containing configuration information
      if (value.mName !== '#startup') {
        // Display a tile for each sequence name....
        const lAppContainer = $('<div class="sequence" style="padding:1rem; margin-bottom: 5px; position: relative; overflow: hidden;">').append(
          $('<div class="axis-column" style="width:40%;padding-top:0.3rem;z-index:1;">').append(
            $('<div class="sequence-name" style="width:100%;left:0;">').text(value.mName || 'Custom Script'),
            $('<div class="instruction-number" style="">').text(nextCurrentPlayingApp.isPythonScript() ? '' : 'Instruction #0'),
          ),
          $('<div class="axis-column" style="width:40%;z-index:1;">').append(
            $('<div class="instruction-name" style="line-height:3rem;">'),
          ),
          $('<div class="axis-column" style="width:20%;z-index:1;">').append(
            $('<div class="sequence-status" style="line-height:3rem;">').text(appControl.gSeqStateEnum.inactive),
          ),
          $('<div class="ml_animated_background" style="z-index:0;">'),
        );

        lGenericContainer.append(lAppContainer);
      }
    });
  }

  // Update current app value
  // Necessary to update when all is complete because timer tries to access currentPlayingApp and can trigger onTrace
  gGlobalVariables.currentPlayingApp = nextCurrentPlayingApp;

  appControl.updateApplicationStatus(gMmAppsIndexes, gGlobalVariables);
}
